<template>
  <v-container fluid class="pa-6">
    <!-- Page Header -->
    <div class="d-flex align-center justify-space-between mb-6">
      <div>
        <h1 class="text-h4 font-weight-bold text-primary">Application Settings</h1>
        <p class="text-body-2 text-medium-emphasis mt-1">
          Configure system-wide preferences and behaviour
        </p>
      </div>
      <div class="d-flex gap-2">
        <v-btn
          variant="outlined"
          color="warning"
          :loading="resetting"
          prepend-icon="mdi-restore"
          @click="confirmReset = true"
        >
          Reset Defaults
        </v-btn>
        <v-btn
          color="primary"
          :loading="saving"
          :disabled="!hasChanges"
          prepend-icon="mdi-content-save"
          @click="saveSettings"
        >
          Save Changes
        </v-btn>
      </div>
    </div>

    <!-- Unsaved changes banner -->
    <v-alert
      v-if="hasChanges"
      type="info"
      variant="tonal"
      density="compact"
      class="mb-4"
      icon="mdi-pencil-circle"
    >
      You have unsaved changes. Click <strong>Save Changes</strong> to apply them.
    </v-alert>

    <!-- Save success snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3500" location="top right">
      <v-icon class="mr-2">{{ snackbar.icon }}</v-icon>
      {{ snackbar.text }}
    </v-snackbar>

    <!-- Loading skeleton -->
    <v-row v-if="loading">
      <v-col v-for="n in 4" :key="n" cols="12" md="6">
        <v-skeleton-loader type="card" />
      </v-col>
    </v-row>

    <!-- Settings Tabs -->
    <v-card v-else elevation="1" rounded="lg">
      <v-tabs v-model="activeTab" color="primary" slider-color="primary">
        <v-tab value="general">
          <v-icon start>mdi-cog</v-icon>
          General
        </v-tab>
        <v-tab value="branding">
          <v-icon start>mdi-palette</v-icon>
          Branding
        </v-tab>
        <v-tab value="meetings">
          <v-icon start>mdi-calendar-account</v-icon>
          Meetings
        </v-tab>
        <v-tab value="security">
          <v-icon start>mdi-shield-lock</v-icon>
          Security
        </v-tab>
        <v-tab value="notifications">
          <v-icon start>mdi-bell</v-icon>
          Notifications
        </v-tab>
      </v-tabs>

      <v-divider />

      <v-window v-model="activeTab" class="pa-6">
        <!-- ── GENERAL ─────────────────────────────────────────────────── -->
        <v-window-item value="general">
          <v-row>
            <v-col cols="12">
              <p class="text-subtitle-1 font-weight-medium mb-4">
                <v-icon class="mr-1" color="primary">mdi-domain</v-icon>
                Organisation Information
              </p>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form['org_name']"
                label="Organisation Name"
                hint="Full legal name of the organisation"
                persistent-hint
                variant="outlined"
                density="comfortable"
                @input="markChanged"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form['org_short_name']"
                label="Short Name / Acronym"
                hint="Used in headings and the sidebar"
                persistent-hint
                variant="outlined"
                density="comfortable"
                @input="markChanged"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form['org_email']"
                label="Official Email"
                hint="Primary contact email"
                persistent-hint
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-email"
                @input="markChanged"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form['org_phone']"
                label="Contact Phone"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-phone"
                @input="markChanged"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form['org_website']"
                label="Website URL"
                hint="Must start with https://"
                persistent-hint
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-web"
                @input="markChanged"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form['org_address']"
                label="Physical Address"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-map-marker"
                @input="markChanged"
              />
            </v-col>

            <v-col cols="12">
              <v-divider class="my-2" />
              <p class="text-subtitle-1 font-weight-medium my-4">
                <v-icon class="mr-1" color="primary">mdi-clock-outline</v-icon>
                Localisation
              </p>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="form['timezone']"
                :items="timezoneOptions"
                label="System Timezone"
                hint="All meeting times are stored in this timezone"
                persistent-hint
                variant="outlined"
                density="comfortable"
                @update:model-value="markChanged"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="form['date_format']"
                :items="dateFormatOptions"
                label="Date Display Format"
                variant="outlined"
                density="comfortable"
                @update:model-value="markChanged"
              />
            </v-col>
          </v-row>
        </v-window-item>

        <!-- ── BRANDING ───────────────────────────────────────────────── -->
        <v-window-item value="branding">
          <v-row>
            <v-col cols="12">
              <p class="text-subtitle-1 font-weight-medium mb-4">
                <v-icon class="mr-1" color="primary">mdi-palette-outline</v-icon>
                Visual Identity
              </p>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form['system_title']"
                label="System Title"
                hint="Shown in the browser tab and page header"
                persistent-hint
                variant="outlined"
                density="comfortable"
                @input="markChanged"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="form['primary_color']"
                label="Primary Brand Colour"
                hint="Hex colour code, e.g. #1565C0"
                persistent-hint
                variant="outlined"
                density="comfortable"
                @input="markChanged"
              >
                <template #prepend-inner>
                  <v-avatar
                    :color="form['primary_color'] || '#1565C0'"
                    size="20"
                    class="mr-1"
                  />
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="form['secondary_color']"
                label="Secondary Colour"
                hint="Hex colour code"
                persistent-hint
                variant="outlined"
                density="comfortable"
                @input="markChanged"
              >
                <template #prepend-inner>
                  <v-avatar
                    :color="form['secondary_color'] || '#1E88E5'"
                    size="20"
                    class="mr-1"
                  />
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12">
              <v-alert type="info" variant="tonal" density="compact" class="mt-2">
                <strong>Note:</strong> Colour and title changes take effect after the next page reload.
              </v-alert>
            </v-col>
          </v-row>
        </v-window-item>

        <!-- ── MEETINGS ───────────────────────────────────────────────── -->
        <v-window-item value="meetings">
          <v-row>
            <v-col cols="12">
              <p class="text-subtitle-1 font-weight-medium mb-4">
                <v-icon class="mr-1" color="primary">mdi-calendar-check</v-icon>
                Meeting Defaults
              </p>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model.number="form['default_meeting_duration']"
                label="Default Duration (minutes)"
                type="number"
                min="15"
                max="480"
                step="15"
                hint="Pre-filled when creating a new meeting"
                persistent-hint
                variant="outlined"
                density="comfortable"
                @input="markChanged"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model.number="form['quorum_percentage']"
                label="Quorum Percentage (%)"
                type="number"
                min="1"
                max="100"
                hint="Minimum % of members required"
                persistent-hint
                variant="outlined"
                density="comfortable"
                @input="markChanged"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model.number="form['meeting_reminder_hours']"
                label="Meeting Reminder (hours before)"
                type="number"
                min="1"
                hint="When to send the pre-meeting reminder"
                persistent-hint
                variant="outlined"
                density="comfortable"
                @input="markChanged"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model.number="form['attendance_lock_hours']"
                label="Attendance Lock Window (hours)"
                type="number"
                min="1"
                hint="Hours after meeting before attendance is auto-locked"
                persistent-hint
                variant="outlined"
                density="comfortable"
                @input="markChanged"
              />
            </v-col>
            <v-col cols="12" md="4" class="d-flex align-center">
              <v-switch
                v-model="form['allow_virtual_meetings']"
                label="Allow Virtual / Hybrid Meetings"
                color="primary"
                hide-details
                @change="markChanged"
              />
            </v-col>
          </v-row>
        </v-window-item>

        <!-- ── SECURITY ───────────────────────────────────────────────── -->
        <v-window-item value="security">
          <v-row>
            <v-col cols="12">
              <p class="text-subtitle-1 font-weight-medium mb-4">
                <v-icon class="mr-1" color="primary">mdi-key</v-icon>
                Password Policy
              </p>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model.number="form['password_min_length']"
                label="Minimum Password Length"
                type="number"
                min="6"
                max="32"
                variant="outlined"
                density="comfortable"
                @input="markChanged"
              />
            </v-col>
            <v-col cols="12" md="4" class="d-flex align-center">
              <v-switch
                v-model="form['password_require_uppercase']"
                label="Require Uppercase Letter"
                color="primary"
                hide-details
                @change="markChanged"
              />
            </v-col>
            <v-col cols="12" md="4" class="d-flex align-center">
              <v-switch
                v-model="form['password_require_number']"
                label="Require Number"
                color="primary"
                hide-details
                @change="markChanged"
              />
            </v-col>
            <v-col cols="12" md="4" class="d-flex align-center">
              <v-switch
                v-model="form['password_require_special']"
                label="Require Special Character"
                color="primary"
                hide-details
                @change="markChanged"
              />
            </v-col>

            <v-col cols="12">
              <v-divider class="my-2" />
              <p class="text-subtitle-1 font-weight-medium my-4">
                <v-icon class="mr-1" color="primary">mdi-login</v-icon>
                Session & Access
              </p>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model.number="form['session_timeout_minutes']"
                label="Session Timeout (minutes)"
                type="number"
                min="15"
                hint="Idle time before automatic logout"
                persistent-hint
                variant="outlined"
                density="comfortable"
                @input="markChanged"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model.number="form['max_login_attempts']"
                label="Max Failed Login Attempts"
                type="number"
                min="3"
                max="20"
                hint="Account locked after this many failures"
                persistent-hint
                variant="outlined"
                density="comfortable"
                @input="markChanged"
              />
            </v-col>
            <v-col cols="12" md="4" class="d-flex align-center">
              <v-switch
                v-model="form['two_factor_enabled']"
                label="Two-Factor Authentication (Admin)"
                color="primary"
                hide-details
                @change="markChanged"
              />
            </v-col>

            <!-- ── Per-user 2FA Management ──────────────────────────── -->
            <v-col cols="12">
              <v-divider class="my-2" />
              <div class="d-flex align-center justify-space-between my-4">
                <p class="text-subtitle-1 font-weight-medium mb-0">
                  <v-icon class="mr-1" color="primary">mdi-cellphone-key</v-icon>
                  Per-User Two-Factor Authentication
                </p>
                <v-btn
                  size="small"
                  variant="tonal"
                  color="primary"
                  prepend-icon="mdi-refresh"
                  :loading="twoFaLoading"
                  @click="load2faUsers"
                >
                  Refresh
                </v-btn>
              </div>

              <v-alert
                v-if="!form['two_factor_enabled']"
                type="info"
                variant="tonal"
                density="compact"
                class="mb-4"
                icon="mdi-information"
              >
                Enable the global 2FA toggle above to allow per-user 2FA assignment.
              </v-alert>

              <v-data-table
                :headers="twoFaHeaders"
                :items="twoFaUsers"
                :loading="twoFaLoading"
                density="comfortable"
                hover
                rounded="lg"
                :items-per-page="10"
              >
                <!-- Status chip -->
                <template #[`item.two_factor_enabled`]="{ item }">
                  <v-chip
                    :color="item.two_factor_enabled ? 'success' : 'default'"
                    size="small"
                    :prepend-icon="item.two_factor_enabled ? 'mdi-shield-check' : 'mdi-shield-off-outline'"
                    label
                  >
                    {{ item.two_factor_enabled ? 'Enabled' : 'Disabled' }}
                  </v-chip>
                </template>

                <!-- Role chip -->
                <template #[`item.role_name`]="{ item }">
                  <v-chip size="x-small" color="primary" variant="outlined" label>
                    {{ item.role }}
                  </v-chip>
                </template>

                <!-- Action button -->
                <template #[`item.actions`]="{ item }">
                  <v-btn
                    :color="item.two_factor_enabled ? 'error' : 'primary'"
                    :prepend-icon="item.two_factor_enabled ? 'mdi-shield-remove' : 'mdi-shield-plus'"
                    size="small"
                    variant="tonal"
                    :loading="item._loading"
                    :disabled="!form['two_factor_enabled']"
                    @click="toggle2fa(item)"
                  >
                    {{ item.two_factor_enabled ? 'Disable' : 'Enable' }}
                  </v-btn>
                </template>

                <!-- Empty state -->
                <template #no-data>
                  <div class="text-center py-8 text-medium-emphasis">
                    <v-icon size="48" color="grey-lighten-1">mdi-account-group-outline</v-icon>
                    <p class="mt-2">No users found</p>
                    <v-btn variant="text" color="primary" size="small" @click="load2faUsers">Load Users</v-btn>
                  </div>
                </template>
              </v-data-table>
            </v-col>
          </v-row>
        </v-window-item>

        <!-- ── NOTIFICATIONS ──────────────────────────────────────────── -->
        <v-window-item value="notifications">
          <v-row>
            <v-col cols="12">
              <div class="d-flex align-center gap-4 mb-4">
                <p class="text-subtitle-1 font-weight-medium mb-0">
                  <v-icon class="mr-1" color="primary">mdi-bell-ring</v-icon>
                  Notification Triggers
                </p>
                <v-switch
                  v-model="form['email_notifications_enabled']"
                  label="Enable Email Notifications"
                  color="primary"
                  hide-details
                  density="compact"
                  class="ml-4"
                  @change="markChanged"
                />
              </div>
            </v-col>

            <v-col cols="12" md="3" class="d-flex align-center">
              <v-switch
                v-model="form['notify_new_meeting']"
                label="New Meeting Scheduled"
                color="primary"
                hide-details
                :disabled="!form['email_notifications_enabled']"
                @change="markChanged"
              />
            </v-col>
            <v-col cols="12" md="3" class="d-flex align-center">
              <v-switch
                v-model="form['notify_new_resolution']"
                label="New Resolution Created"
                color="primary"
                hide-details
                :disabled="!form['email_notifications_enabled']"
                @change="markChanged"
              />
            </v-col>
            <v-col cols="12" md="3" class="d-flex align-center">
              <v-switch
                v-model="form['notify_voting_reminder']"
                label="Voting Deadline Reminder"
                color="primary"
                hide-details
                :disabled="!form['email_notifications_enabled']"
                @change="markChanged"
              />
            </v-col>
            <v-col cols="12" md="3" class="d-flex align-center">
              <v-switch
                v-model="form['notify_new_document']"
                label="New Document Uploaded"
                color="primary"
                hide-details
                :disabled="!form['email_notifications_enabled']"
                @change="markChanged"
              />
            </v-col>

            <v-col cols="12">
              <v-divider class="my-2" />
              <p class="text-subtitle-1 font-weight-medium my-4">
                <v-icon class="mr-1" color="primary">mdi-email-fast</v-icon>
                SMTP / Email Configuration
              </p>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form['smtp_host']"
                label="SMTP Host"
                hint="e.g. smtp.gmail.com"
                persistent-hint
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-server"
                @input="markChanged"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                v-model.number="form['smtp_port']"
                label="SMTP Port"
                type="number"
                variant="outlined"
                density="comfortable"
                @input="markChanged"
              />
            </v-col>
            <v-col cols="12" md="3" class="d-flex align-center">
              <v-switch
                v-model="form['smtp_use_tls']"
                label="Use TLS"
                color="primary"
                hide-details
                @change="markChanged"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form['smtp_username']"
                label="SMTP Username"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-account"
                @input="markChanged"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form['email_from_name']"
                label="From Name"
                hint="Sender name in outgoing emails"
                persistent-hint
                variant="outlined"
                density="comfortable"
                @input="markChanged"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form['email_from_address']"
                label="From Email Address"
                hint="Reply-to / sender address"
                persistent-hint
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-email"
                @input="markChanged"
              />
            </v-col>
          </v-row>
        </v-window-item>
      </v-window>
    </v-card>

    <!-- Reset to Defaults Confirmation Dialog -->
    <v-dialog v-model="confirmReset" max-width="440">
      <v-card rounded="lg">
        <v-card-title class="text-h6 pa-6 pb-2">
          <v-icon color="warning" class="mr-2">mdi-restore-alert</v-icon>
          Reset to Defaults?
        </v-card-title>
        <v-card-text class="px-6 pb-4 text-body-2 text-medium-emphasis">
          This will overwrite <strong>all</strong> settings with their factory defaults.
          This action cannot be undone.
        </v-card-text>
        <v-card-actions class="pa-6 pt-0">
          <v-spacer />
          <v-btn variant="text" @click="confirmReset = false">Cancel</v-btn>
          <v-btn color="warning" variant="elevated" :loading="resetting" @click="resetDefaults">
            Yes, Reset
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import api from '@/services/api'

export default {
  name: 'Settings',
  setup() {
    const loading   = ref(true)
    const saving    = ref(false)
    const resetting = ref(false)
    const activeTab = ref('general')
    const hasChanges = ref(false)
    const confirmReset = ref(false)

    const snackbar = reactive({ show: false, text: '', color: 'success', icon: 'mdi-check-circle' })

    // Flat form model — keys map 1-to-1 with SystemSetting.key
    const form = reactive({})

    const timezoneOptions = [
      'Africa/Banjul', 'Africa/Dakar', 'Africa/Abidjan',
      'Europe/London', 'Europe/Paris', 'Europe/Berlin',
      'America/New_York', 'America/Chicago', 'America/Los_Angeles',
      'Asia/Dubai', 'Asia/Kolkata', 'UTC'
    ]

    const dateFormatOptions = [
      { title: 'DD/MM/YYYY  (e.g. 20/02/2026)', value: 'DD/MM/YYYY' },
      { title: 'MM/DD/YYYY  (e.g. 02/20/2026)', value: 'MM/DD/YYYY' },
      { title: 'YYYY-MM-DD  (e.g. 2026-02-20)', value: 'YYYY-MM-DD' },
      { title: 'D MMM YYYY  (e.g. 20 Feb 2026)', value: 'D MMM YYYY' }
    ]

    const loadSettings = async () => {
      loading.value = true
      try {
        const { data } = await api.get('/settings')
        // Backend returns { settings: { category: [{ key, value, ... }] } }
        const grouped = data.settings || {}
        Object.values(grouped).flat().forEach(s => {
          form[s.key] = s.value
        })
      } catch (e) {
        notify('Failed to load settings', 'error', 'mdi-alert-circle')
      } finally {
        loading.value = false
      }
    }

    const markChanged = () => { hasChanges.value = true }

    const saveSettings = async () => {
      saving.value = true
      try {
        await api.put('/settings', { settings: { ...form } })
        hasChanges.value = false
        notify('Settings saved successfully', 'success', 'mdi-check-circle')
      } catch (e) {
        notify('Failed to save settings', 'error', 'mdi-alert-circle')
      } finally {
        saving.value = false
      }
    }

    const resetDefaults = async () => {
      resetting.value = true
      try {
        await api.post('/settings/reset')
        confirmReset.value = false
        hasChanges.value = false
        await loadSettings()
        notify('Settings reset to defaults', 'success', 'mdi-restore')
      } catch (e) {
        notify('Failed to reset settings', 'error', 'mdi-alert-circle')
      } finally {
        resetting.value = false
      }
    }

    const notify = (text, color = 'success', icon = 'mdi-check-circle') => {
      snackbar.text  = text
      snackbar.color = color
      snackbar.icon  = icon
      snackbar.show  = true
    }

    // ── Per-user 2FA management ──────────────────────────────────────
    const twoFaUsers   = ref([])
    const twoFaLoading = ref(false)
    const twoFaHeaders = [
      { title: 'Name',   key: 'full_name',             sortable: true  },
      { title: 'Email',  key: 'email',                sortable: true  },
      { title: 'Role',   key: 'role_name',             sortable: true  },
      { title: '2FA',    key: 'two_factor_enabled',   sortable: true  },
      { title: 'Action', key: 'actions',              sortable: false }
    ]

    const load2faUsers = async () => {
      twoFaLoading.value = true
      try {
        const { data } = await api.get('/users/2fa/status')
        twoFaUsers.value = (data.users || []).map(u => ({ ...u, _loading: false }))
      } catch (e) {
        notify('Failed to load 2FA user list', 'error', 'mdi-alert-circle')
      } finally {
        twoFaLoading.value = false
      }
    }

    const toggle2fa = async (user) => {
      user._loading = true
      const enable = !user.two_factor_enabled
      try {
        const endpoint = `/users/${user.id}/2fa/${enable ? 'enable' : 'disable'}`
        await api.post(endpoint)
        user.two_factor_enabled = enable
        notify(
          `2FA ${enable ? 'enabled' : 'disabled'} for ${user.name}`,
          enable ? 'success' : 'warning',
          enable ? 'mdi-shield-check' : 'mdi-shield-off'
        )
      } catch (e) {
        notify(`Failed to update 2FA for ${user.name}`, 'error', 'mdi-alert-circle')
      } finally {
        user._loading = false
      }
    }

    onMounted(async () => {
      await loadSettings()
      await load2faUsers()
    })

    return {
      loading, saving, resetting, activeTab, hasChanges, confirmReset,
      form, snackbar,
      timezoneOptions, dateFormatOptions,
      markChanged, saveSettings, resetDefaults,
      twoFaUsers, twoFaLoading, twoFaHeaders, load2faUsers, toggle2fa
    }
  }
}
</script>
